import { CheckboxField } from '@clay-run/glaze/checkbox-field';
import { Strong, Text } from '@clay-run/glaze/text';

/**
 * U4b fix (plan 2026-09-30-002-fix) — the lead-only half of V1's
 * `GongFlowStepsSection` that `StepRows` never ported: the "Override
 * cool-off" checkbox and the "Enrolls as you" owner note
 * (`EnrollmentItemEditor.tsx`'s `GongFlowStepsSection`, `:449-470`). Lives as
 * its own file (not inlined in `StepRows.tsx`) per this unit's scope — a
 * sibling component `StepRows` renders beneath its step rows, lead scope
 * only (`BulkEditor`'s batch scope passes no `coolOff` prop and never
 * mounts this).
 *
 * Copy is byte-identical to V1's — see the literal sentence below and the
 * owner-note's JSX structure, both kept matching so `tdd-verifier`/QA can
 * diff them directly against `GongFlowStepsSection`.
 */
export interface LeadGongControlsProps {
  /** `item.id` — feeds the stable `cooloff-${itemId}` id V1's checkbox used. */
  itemId: number;
  /** `item.cool_off_override` — server state; never a local draft (no `useState`). */
  checked: boolean;
  /** `locked` from `EnrollmentItemEditor` (E26/E27/D34) — readOnly/busy/queued/sending all land here. */
  disabled: boolean;
  /** `lockedTitle` — same disabled-reason tooltip V1's `<input title={lockedTitle}>` showed. */
  disabledTitle?: string;
  onToggle: (next: boolean) => void;
  /** `useUserControlsContext().email`, resolved by the caller (no context read in here). */
  actingRepEmail: string | null;
  /** `useGongFlowStepsForced(flowId).data.flow_owner_name`, resolved by the caller. */
  flowOwnerName: string | null;
}

/** Exact decided copy (spec handoff 8/19) — kept as ONE literal string, same as V1. */
const COOL_OFF_LABEL =
  'Override cool-off — enroll even though this contact recently finished or left another flow.';

export function LeadGongControls({
  itemId,
  checked,
  disabled,
  disabledTitle,
  onToggle,
  actingRepEmail,
  flowOwnerName,
}: LeadGongControlsProps) {
  return (
    <div className="mt-2">
      {/* U4b fix (round 2, G1/P2) — Glaze's `CheckboxField` renders a
          `<label>` plus a Base UI `CheckboxPrimitive.Root` whose visible
          element is a `<span role="checkbox">`; neither matches
          `RailRow`'s `INTERACTIVE_SELECTOR` on its own, so a click on the
          label or the box bubbled to the row and collapsed it instead of
          (or in addition to) toggling cool-off. `data-no-row-toggle` has to
          live on this wrapper `div`, not on `CheckboxField` itself —
          `extractFormControlProps` splits the field's props and would land
          an arbitrary prop on the span, not the label. The "Enrolls as
          you" note below stays OUTSIDE this wrapper: it's non-interactive
          body text and falls through to the row toggle like every other
          text line in this expanded body. */}
      <div data-no-row-toggle>
        <CheckboxField
          id={`cooloff-${itemId}`}
          checked={checked}
          disabled={disabled}
          title={disabledTitle}
          onCheckedChange={next => onToggle(next)}
          label={COOL_OFF_LABEL}
        />
      </div>
      <Text size="xs" variant="tertiary" className="mt-2">
        Enrolls as <Strong>you</Strong>
        {actingRepEmail ? ` (${actingRepEmail})` : ''} — you own the flow to-dos in Gong.
        {flowOwnerName && ` Flow owner: ${flowOwnerName}.`}
      </Text>
    </div>
  );
}
